import { Check, Copy, KeyRound, Shield, Trash2, UserPlus } from "lucide-react";
import { useState, type FormEvent } from "react";
import { roleLabels, type Role, type User } from "@lovmia/shared";
import { Button, EmptyState, PageHeader, Select } from "../components/Ui";
import { useAppState } from "../state";

/** Временный пароль для новой учётки: 12 символов без похожих друг на друга. */
function generatePassword() {
  const alphabet = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = crypto.getRandomValues(new Uint32Array(12));
  return Array.from(bytes, (value) => alphabet[value % alphabet.length]).join("");
}

const empty = { name: "", email: "", password: "", role: "user" as Role };

function MemberRow({ member }: { member: User }) {
  const { data, act, notify } = useAppState();
  const [busy, setBusy] = useState<string | null>(null);
  const isSelf = member.id === data.user.id;
  const admins = data.team.filter((user) => user.role === "admin").length;
  const isLastAdmin = member.role === "admin" && admins < 2;

  const patch = async (body: Record<string, unknown>, success: string, key: string) => {
    setBusy(key);
    try { await act(`/api/team/${member.id}`, { method: "PATCH", body: JSON.stringify(body) }, success); }
    finally { setBusy(null); }
  };

  const resetPassword = async () => {
    const password = generatePassword();
    await patch({ password }, `Пароль ${member.name} обновлён`, "password");
    // Показываем один раз: в базе лежит только хеш, второй раз этот пароль не достать.
    try {
      await navigator.clipboard.writeText(password);
      notify("Новый пароль скопирован", `${password} — передайте лично, сессии сброшены`, "success");
    } catch {
      notify("Новый пароль", `${password} — скопируйте вручную, сессии сброшены`, "success");
    }
  };

  const remove = async () => {
    setBusy("remove");
    try { await act(`/api/team/${member.id}`, { method: "DELETE" }, `${member.name} больше не в воркспейсе`); }
    finally { setBusy(null); }
  };

  return <div className="team-row">
    <span className="avatar">{member.avatar}</span>
    <div className="team-row__who">
      <strong>{member.name}{isSelf && <em> · это вы</em>}</strong>
      <small>{member.email}</small>
    </div>
    <Select
      value={member.role}
      disabled={busy !== null || isLastAdmin}
      title={isLastAdmin ? "Последний админ — сначала назначьте другого" : undefined}
      onChange={(event) => patch({ role: event.target.value }, `${member.name}: роль изменена`, "role")}
    >
      {(["admin", "user"] as Role[]).map((role) => <option key={role} value={role}>{roleLabels[role]}</option>)}
    </Select>
    <div className="team-row__actions">
      <Button variant="ghost" loading={busy === "password"} onClick={resetPassword} title="Сгенерировать новый пароль и закрыть активные сессии">
        <KeyRound size={15} /> Пароль
      </Button>
      <Button
        variant="ghost"
        loading={busy === "remove"}
        disabled={isSelf || isLastAdmin}
        title={isSelf ? "Себя удалить нельзя" : isLastAdmin ? "Последний админ" : "Убрать из воркспейса"}
        onClick={remove}
      ><Trash2 size={15} /></Button>
    </div>
  </div>;
}

export function TeamPage() {
  const { data, act, notify } = useAppState();
  const [draft, setDraft] = useState(empty);
  const [busy, setBusy] = useState(false);

  if (data.user.role !== "admin") {
    return <>
      <PageHeader eyebrow="team" title="Команда" />
      <EmptyState icon="alert" title="Раздел для админов" text="Учётки заводит и меняет админ воркспейса." />
    </>;
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const password = draft.password || generatePassword();
      await act("/api/team", { method: "POST", body: JSON.stringify({ ...draft, password }) }, `${draft.name} добавлен`);
      if (!draft.password) {
        try {
          await navigator.clipboard.writeText(password);
          notify("Пароль скопирован", `${password} — передайте лично`, "success");
        } catch {
          notify("Временный пароль", password, "success");
        }
      }
      setDraft(empty);
    } finally {
      setBusy(false);
    }
  };

  const fillPassword = async () => {
    const password = generatePassword();
    setDraft((current) => ({ ...current, password }));
    try { await navigator.clipboard.writeText(password); notify("Пароль скопирован", password, "success"); } catch { /* буфер недоступен — пароль виден в поле */ }
  };

  return <>
    <PageHeader
      eyebrow="team & access"
      title="Команда"
      description="Админ решает и настраивает контур, пользователь работает с крео: заливка, вариации, запуск и стоп."
      actions={<div className="header-stat"><strong>{data.team.length}</strong><span>аккаунта</span></div>}
    />
    <div className="team-layout">
      <form className="team-create team-create--page" onSubmit={submit}>
        <div className="team-create__head"><UserPlus size={16} /><strong>Новая учётная запись</strong></div>
        <label><span>Имя</span>
          <input required minLength={2} value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} placeholder="Никита" />
        </label>
        <label><span>Email</span>
          <input required type="email" value={draft.email} onChange={(event) => setDraft({ ...draft, email: event.target.value })} placeholder="name@team.com" />
        </label>
        <label><span>Временный пароль</span>
          <div className="team-create__password">
            <input minLength={8} value={draft.password} onChange={(event) => setDraft({ ...draft, password: event.target.value })} placeholder="сгенерируем сами" />
            <Button type="button" variant="ghost" onClick={fillPassword} title="Сгенерировать и скопировать"><Copy size={14} /></Button>
          </div>
        </label>
        <label><span>Роль</span>
          <Select value={draft.role} onChange={(event) => setDraft({ ...draft, role: event.target.value as Role })}>
            {(["user", "admin"] as Role[]).map((role) => <option key={role} value={role}>{roleLabels[role]}</option>)}
          </Select>
        </label>
        <Button type="submit" variant="primary" loading={busy}><Check size={15} /> Создать</Button>
        <small className="team-create__hint">Тот же аккаунт используется при подключении чата по MCP.</small>
      </form>

      <section className="team-roster">
        {data.team.map((member) => <MemberRow key={member.id} member={member} />)}
      </section>
    </div>
    <p className="team-note"><Shield size={14} /> Права админа: гипотезы и концепты, вердикты и скейл, learnings, пороги воркспейса, учётки. Всё остальное доступно обеим ролям.</p>
  </>;
}
